import React from "react";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";

import useActions, { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearPreview,
  selectFileFolders,
  selectFileListFullLength,
  selectFileListPageSize,
  selectFileListPageStart,
  selectFileLineCounts,
  selectFiles,
  selectFilteredDashes,
  selectFilteredFileFolders,
  selectFilteredFileSizes,
  selectFilteredFiles,
  selectFilteredFolders,
  selectFolderName,
  selectFolderStack,
  selectLibrary,
  selectRepoFolder,
  selectRepoId,
  selectRepos,
  selectReposMode,
  selectRoleId,
  selectRoles,
  selectRolesMode,
  selectScenarioId,
  selectScenarioMeasurables,
  selectScenariosMode,
  selectUsersInRole,
  setError,
} from "./librarySlice";
import ImageList from "@mui/material/ImageList";
import Toolbar from "@mui/material/Toolbar";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import DashLibraryItem from "./DashLibraryItem";
import DashLibraryFolder from "./DashLibraryFolder";
import DashLibraryRolesSpecial from "./DashLibraryRolesSpecial";
import DashLibraryReposSpecial from "./DashLibraryReposSpecial";
import DashLibraryScenariosSpecial from "./DashLibraryScenariosSpecial";
import DashLibraryRole from "./DashLibraryRole";
import DashLibraryRepo from "./DashLibraryRepo";
import DashLibraryScenario from "./DashLibraryScenario";
import ScenarioMeasurablesSheet from "./ScenarioMeasurablesSheet";
import DashLibraryFile from "./DashLibraryFile";
import DashLibraryFileFolder from "./DashLibraryFileFolder";
import DashLibraryUser from "./DashLibraryUser";
//import { selectDashPreview } from "./librarySlice";
import PreviewModal from "../preview/PreviewModal";
import { selectCurrentWorkspace, selectCurrentWorkspaceRole, selectWorkspaces } from "../auth/authSlice";
import { LibraryHeaderMenu } from "./header/LibraryHeaderMenu";
import { Dash } from "../../types/Dash";
import { Workspace } from "../../types/User";
import { Folder } from "../../types/Folder";
import BackgroundTasksPanel from "../ide/BackgroundTasksPanel";
import IdeHistoryPanel from "../ide/IdeHistoryPanel";
import Gears from "./gears";
import CircularProgress from "@mui/material/CircularProgress";

export default function Library() {
  const librarySlice = useAppSelector(selectLibrary);
  const filteredDashes = useAppSelector(
    selectFilteredDashes
  );
  const filteredFolders = useAppSelector(
    selectFilteredFolders
  );
  const rolesMode = useAppSelector(
    selectRolesMode
  );
  const reposMode = useAppSelector(
    selectReposMode
  );
  const scenariosMode = useAppSelector(
    selectScenariosMode
  );
  const viewRoleId = useAppSelector(
    selectRoleId
  );
  const viewRepoId = useAppSelector(
    selectRepoId
  );
  const viewScenarioId = useAppSelector(
    selectScenarioId
  );
  const scenarioMeasurables = useAppSelector(
    selectScenarioMeasurables
  );
  const folderStack = useAppSelector(
    selectFolderStack
  );

  const dispatch = useAppDispatch();
  //const documentPreview = useAppSelector(selectDocumentPreview);
  const currentWorkspaceRole = useAppSelector(selectCurrentWorkspaceRole);
  const workspaces = useAppSelector(selectWorkspaces);
  const roles = useAppSelector(selectRoles);
  const repos = useAppSelector(selectRepos);
  const files = useAppSelector(selectFilteredFiles);
  const fileSizeIdxs = useAppSelector(selectFilteredFileSizes);
  const fileFolders = useAppSelector(selectFilteredFileFolders);
  const folderName = useAppSelector(selectFolderName);
  const usersInRole = useAppSelector(selectUsersInRole);
  const currentWorkspace = useAppSelector(selectCurrentWorkspace) as Workspace;
  const fileListFullLength = useAppSelector(selectFileListFullLength);
  const fileListPageStart = useAppSelector(selectFileListPageStart);
  const fileListPageSize = useAppSelector(selectFileListPageSize);
  const actions = useActions();

  // When browsing a repo, the "Filter" box searches file/folder names across the
  // WHOLE current directory (not just the loaded page). Re-fetch a filtered,
  // first-page listing from the backend whenever the filter changes (debounced).
  // The client-side selectors still filter the loaded page for instant feedback
  // while this request is in flight.
  const prevFilterRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    // Skip the first run so we don't clobber the initial file_list load.
    if (prevFilterRef.current === null) {
      prevFilterRef.current = librarySlice.filterString;
      return;
    }
    if (prevFilterRef.current === librarySlice.filterString) return;
    prevFilterRef.current = librarySlice.filterString;

    if (!reposMode || viewRepoId <= 0) return;

    const handle = setTimeout(() => {
      actions.fileList({
        id: viewRepoId,
        folder: folderName,
        page_start: 0,
        page_size: fileListPageSize,
        filter: librarySlice.filterString,
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [librarySlice.filterString, reposMode, viewRepoId, folderName, fileListPageSize]);

  // Lazy, background line-count enrichment: if the user loiters on a page of repo
  // files, count lines for the visible text files and show it after the size. Only
  // the files currently on screen are fetched — other pages are never touched. The
  // timer is cleared if the user leaves/changes the view before it fires.
  const lineCounts = useAppSelector(selectFileLineCounts);
  const filesKey = files.join("|");
  React.useEffect(() => {
    if (!reposMode || viewRepoId <= 0 || files.length === 0) return;

    const locations = files.map((f) => (folderName === "/" ? f : folderName + "/" + f));
    const missing = locations.filter((loc) => lineCounts[loc] === undefined);
    if (missing.length === 0) return;

    const handle = setTimeout(() => {
      actions.getLineCounts({ repoId: viewRepoId, files: missing });
    }, 1500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesKey, folderName, viewRepoId, reposMode, lineCounts]);

  function handlePreviewClose() {
    dispatch(clearPreview());
  }

  const roleFilteredDashes = filteredDashes.filter(_ => {
      return true;
  });

  const roleFilteredFolders = filteredFolders.filter(_ => {
    return true;
  });

  //console.log("A", fileFolders);

  const currentScenario = currentWorkspace?.scenarios?.find((s) => s.id === viewScenarioId);

  let content = <div></div>;

  if (scenariosMode && viewScenarioId > 0) {
    content = (
      <Box sx={{ width: "100%" }}>
        <ScenarioMeasurablesSheet
          scenarioId={viewScenarioId}
          measurables={scenarioMeasurables}
          loading={librarySlice.status === "loading"}
          scenarioName={currentScenario?.name}
          scenarioFolderId={currentScenario?.folder_id ?? undefined}
        />
      </Box>
    );
  } else if (librarySlice.searchMode == false || viewRepoId <= 0) {
    content = (
      <Box sx={{width: "100%"}}>
        <ImageList cols={reposMode ? 8 : 6}>
          { !rolesMode && !reposMode && !scenariosMode && currentWorkspaceRole == "ADMIN" && folderStack.length == 0 && 
            <DashLibraryRolesSpecial
            key={"roles_special_1"}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
          />
          }
          { !rolesMode && !reposMode && !scenariosMode && currentWorkspaceRole == "ADMIN" && folderStack.length == 0 && 
            <DashLibraryReposSpecial
            key={"roles_special_2"}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
          />
          }
          { !rolesMode && !reposMode && !scenariosMode && currentWorkspaceRole == "ADMIN" && folderStack.length == 0 && 
            <DashLibraryScenariosSpecial
            key={"scenarios_special_1"}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
          />
          }
          { !rolesMode && !reposMode && !scenariosMode && roleFilteredFolders.map((folder) => (
              <DashLibraryFolder
                key={folder.id}
                folder={folder as Folder}
                workspaceRole={currentWorkspaceRole}
                workspaces={workspaces}
                currentWorkspace={currentWorkspace}
                favourite={false}
                roles={roles}
              />)) }

          { !rolesMode && !reposMode && !scenariosMode && roleFilteredDashes.map((dash) => (
              <DashLibraryItem
                key={dash.id}
                dash={dash as Dash}
                workspaceRole={currentWorkspaceRole}
                workspaces={workspaces}
                currentWorkspace={currentWorkspace}
                favourite={false}
                roles={roles}
              />)) }
          { rolesMode && viewRoleId < 0 && roles.map((role) => (
            <DashLibraryRole
            key={"role_" + role.id}
            role={role}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
          />
          ))}
          { rolesMode && viewRoleId > 0 && usersInRole.map((user) => (
            <DashLibraryUser
            key={"user_" + user.id}
            user={user}
            roleId={viewRoleId}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
          />
          ))}

          { reposMode && viewRepoId < 0 && currentWorkspace.repos !== null && repos.map((repo) => (
            <DashLibraryRepo
            key={"repo_" + repo.id}
            repo={repo}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
          />
          ))}

          { scenariosMode && currentWorkspace?.scenarios != null && currentWorkspace.scenarios.map((scenario) => (
            <DashLibraryScenario
            key={"scenario_" + scenario.id}
            scenario={scenario}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
          />
          ))}
        
        </ImageList>

        { /* System status report */}
        { viewRepoId > 0 && reposMode && <Box sx={{float: "right", width: "calc(30vw - 50px)"}}>
          <List> 
            <ListItem sx={{bgcolor: 'background.paper', marginBottom: "10px"}}>
              <ListItemText primary={
                <div>
                  <p>FOLDER GIT LOGS</p>
                  <pre style={{fontSize: '0.6em', whiteSpace: 'pre-wrap' }}>
                    { librarySlice.gitFolderInfo}
                  </pre>
                </div>
                }></ListItemText>
            </ListItem>
            { /*<ListItem sx={{bgcolor: 'background.paper', marginBottom: "10px"}}>
              <ListItemText primary={<p>OKAY</p>}></ListItemText>
            </ListItem> */}
          </List>
        </Box> }


        { viewRepoId > 0 && reposMode && (
        <>
        { fileListFullLength > fileListPageSize && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              Showing {fileListPageStart + 1}–{Math.min(fileListPageStart + fileListPageSize, fileListFullLength)} of {fileListFullLength}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={fileListPageStart <= 0}
              onClick={() => actions.fileList({ id: viewRepoId, folder: folderName, page_start: 0, page_size: fileListPageSize, filter: librarySlice.filterString })}
            >
              First page
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={fileListPageStart <= 0}
              onClick={() => actions.fileList({ id: viewRepoId, folder: folderName, page_start: Math.max(0, fileListPageStart - fileListPageSize), page_size: fileListPageSize, filter: librarySlice.filterString })}
            >
              Previous
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={fileListPageStart + fileListPageSize >= fileListFullLength}
              onClick={() => actions.fileList({ id: viewRepoId, folder: folderName, page_start: fileListPageStart + fileListPageSize, page_size: fileListPageSize, filter: librarySlice.filterString })}
            >
              Next
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={fileListPageStart + fileListPageSize >= fileListFullLength}
              onClick={() => actions.fileList({ id: viewRepoId, folder: folderName, page_start: Math.max(0, fileListFullLength - fileListPageSize), page_size: fileListPageSize, filter: librarySlice.filterString })}
            >
              Last page
            </Button>
          </Box>
        )}
        <List sx={{ width: '100%', maxWidth: 360 }}>
          {  fileFolders.map((fileFolder) => (
            <DashLibraryFileFolder
            key={"repo_folder_" + fileFolder}
            file_folder={fileFolder}
            repoId={viewRepoId}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
            folderName={folderName}
          />
          ))}

          {  files.map((file, index) => (
            <DashLibraryFile
            key={"repo_file_" + file}
            file={file}
            size={librarySlice.fileSizes[fileSizeIdxs[index]]}
            repoId={viewRepoId}
            workspaceRole={currentWorkspaceRole}
            workspaces={workspaces}
            currentWorkspace={currentWorkspace}
            folderName={folderName}
          />
          ))}
        </List>
        </>
        ) }
        { viewRepoId > 0 && fileFolders.length == 0 && files.length == 0 && librarySlice.filterString.length > 0 && (
          <p>No files or folders matching criteria</p>
        )}
        { viewRepoId > 0 && fileFolders.length == 0 && files.length == 0 && librarySlice.filterString.length == 0 && (
          librarySlice.isFileListRefreshing ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress size={64} />
            </Box>
          ) : (
            <p>No files or folders in this folder</p>
          )
        )}
      </Box>
    );
  }
  else {
    content = <Box><List sx={{ width: '100%', maxWidth: 360 }}>
            {  librarySlice.searchResult.map((file) => (
              <DashLibraryFile
              key={"repo_file_" + file}
              file={file.path}
              size={0}
              repoId={viewRepoId}
              workspaceRole={currentWorkspaceRole}
              workspaces={workspaces}
              currentWorkspace={currentWorkspace}
              folderName={folderName}
            />
            ))}
          </List>
          { librarySlice.searchResult.length == 0 && <p>No results found</p> }
        </Box>;
  }

  // TODO: https://stackoverflow.com/questions/69383051/dynamically-adjust-imagelist-columns-based-on-screen-size?noredirect=1&lq=1
  return (
    <Box sx={{ bgcolor: grey[200], px: 5, py: 1, height: "100vh" }}>
      {/* Slightly awkward way to pad the top as per MUI guidelines */}
      <Toolbar />
      <LibraryHeaderMenu />
      {librarySlice.error !== undefined ? (
        <Alert
          severity="error"
          onClose={() => {
            dispatch(setError(undefined));
          }}
        >
          {librarySlice.error}
        </Alert>
      ) : null}
      <Box sx={{ bgcolor: grey[200], 
        flexGrow: 2, 
        width: "100%",            display: "flex",
        flexDirection: "column",
        // height: "87vh",
        overflow: "hidden",
        // overflowY: "scroll",
}}>
        {/* <Box
          sx={{
            bgcolor: grey[400],
            px: 5,
            display: "flex",
            flexDirection: "column",
            height: "87vh",
            overflow: "hidden",
            overflowY: "scroll",
          }}
        > */}
          {content}
        {/* </Box> */}
      </Box>
      <BackgroundTasksPanel />
      <IdeHistoryPanel />
      <Gears />
      { /*documentPreview != null ? (
        <PreviewModal
          setParentOpenHandler={handlePreviewClose}
          preview={documentPreview!}
        ></PreviewModal>
      ) : */ null}
    </Box>
  );
}
